import { useEffect, useRef, useState } from "react";
import { Play, Pause, Loader2 } from "lucide-react";
import { API } from "../api";

export default function AudioPlayer({ id, compact, sourcePath = "" }) {
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const ref = useRef(null);
  const sourceRef = useRef("");
  const loadingPromise = useRef(null);

  const clearSource = () => {
    const audio = ref.current;
    if (audio) { audio.pause(); audio.removeAttribute("src"); audio.load(); }
    sourceRef.current = "";
  };

  const waitForPlayable = (audio, url) => new Promise((resolve, reject) => {
    let settled = false;
    const cleanup = () => {
      clearTimeout(timeout);
      audio.removeEventListener("canplay", ready);
      audio.removeEventListener("error", failed);
    };
    const fail = message => {
      if (settled) return;
      settled = true;
      cleanup();
      if (sourceRef.current === url) clearSource();
      reject(new Error(message));
    };
    const ready = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(audio);
    };
    const failed = () => fail("Audio file could not be decoded");
    const timeout = setTimeout(() => fail("Audio loading timed out"), 10000);
    audio.addEventListener("canplay", ready, { once: true });
    audio.addEventListener("error", failed, { once: true });
    audio.src = url;
    audio.load();
    if (audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) ready();
  });

  const load = async () => {
    if (sourceRef.current) return ref.current;
    if (loadingPromise.current) return loadingPromise.current;
    setLoading(true);
    setError("");
    loadingPromise.current = (async () => {
      const audio = ref.current;
      if (!audio) throw new Error("Audio player is unavailable");
      const source = sourcePath || `${API}/files/${encodeURIComponent(id)}`;
      sourceRef.current = source;
      return await waitForPlayable(audio, source);
    })();
    try { return await loadingPromise.current; }
    catch (e) { setError(e.message || "Audio could not load. Please try again."); throw e; }
    finally { loadingPromise.current = null; setLoading(false); }
  };

  useEffect(() => {
    const stopForAnotherPlayer = e => {
      if (e.detail !== id && ref.current && !ref.current.paused) ref.current.pause();
    };
    window.addEventListener("voicehire:audio-play", stopForAnotherPlayer);
    return () => { window.removeEventListener("voicehire:audio-play", stopForAnotherPlayer); clearSource(); };
  }, [id]);

  const toggle = async () => {
    const audio = ref.current;
    if (!audio) return;
    if (!audio.paused) { audio.pause(); return; }
    try {
      const player = await load();
      window.dispatchEvent(new CustomEvent("voicehire:audio-play", { detail: id }));
      await player.play();
    } catch (e) {
      if (e?.name !== "AbortError") setError("Audio could not play. Please try again.");
      setPlaying(false);
    }
  };

  const fmt = (s) => { if (!s || isNaN(s)) return "0:00"; const m = Math.floor(s / 60); const sec = Math.floor(s % 60); return `${m}:${sec.toString().padStart(2, "0")}`; };
  const bars = compact ? 16 : 26;

  return (
    <div className="audio-player" data-testid={`audio-player-${id}`}>
      <audio ref={ref} preload="metadata" onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => { setPlaying(false); setProgress(0); }} onLoadedMetadata={e => setDuration(e.target.duration)} onTimeUpdate={e => setProgress(e.target.currentTime)} onError={() => { setError("Audio could not play. Please try again."); setPlaying(false); }} />
      <button data-testid={`audio-play-${id}`} className="play-btn" onClick={toggle} disabled={loading} aria-label="Play audio">
        {loading ? <Loader2 size={14} className="animate-spin" /> : playing ? <Pause size={14} fill="currentColor" /> : <Play size={14} fill="currentColor" />}
      </button>
      <div className="wave">
        {Array.from({ length: bars }).map((_, i) => {
          const active = playing && duration > 0 && (i / bars) <= (progress / duration);
          return <i key={i} style={{ height: `${8 + ((i * 17) % 14)}px` }} className={active ? "active" : ""} />;
        })}
      </div>
      <span className="audio-time">{playing ? fmt(progress) : fmt(duration)}</span>
      {error && <span className="audio-error" data-testid={`audio-error-${id}`}>{error}</span>}
    </div>
  );
}
